import { sdk } from "@/lib/config"
import client, { unifiedClient, UNIFIED_PRODUCTS_INDEX } from "@/lib/meilisearch"
import { filterListedCollectionIds } from "@/lib/data/collections"

/** Trgovina's own product index (B2C Meilisearch). Search page filters and counts run against it. */
const B2C_PRODUCTS_INDEX = "products"
/** The wholesale shop's own product index on the B2B Meilisearch (unifiedClient host). */
const B2B_PRODUCTS_INDEX = "products"

/**
 * Test MeiliSearch connection and verify index exists.
 * Uses unifiedClient (B2B/products_unified) since we test product search — hybrid: B2C client for categories/collections only.
 */
export const testMeiliSearchConnection = async () => {
  try {
    // Test connection to the unified/B2B Meilisearch (product search)
    const health = await unifiedClient.health()
    
    // Test index exists
    const index = unifiedClient.index(UNIFIED_PRODUCTS_INDEX)
    const indexInfo = await index.getStats()
    
    // Test a simple search
    const testSearch = await index.search("", { limit: 1 })
    
    return {
      connected: true,
      indexExists: true,
      documentsCount: indexInfo.numberOfDocuments,
      hasData: testSearch.hits.length > 0
    }
  } catch (error) {
    return {
      connected: false,
      indexExists: false,
      documentsCount: 0,
      hasData: false,
      error: error instanceof Error ? error.message : "Unknown error"
    }
  }
}

export const getProductsCount = async (params: SearchParams): Promise<number> => {
  const index = client.index(B2C_PRODUCTS_INDEX)
  const filters = buildMeilisearchFilters(params)
  const filterString = filters.length > 0 ? filters.join(" AND ") : undefined

  try {
    const response = await index.search(params.q ?? "", {
      limit: 0,
      filter: filterString,
    })
    return response.estimatedTotalHits ?? 0
  } catch {
    return 0
  }
}

export   interface SearchParams {
  q?: string
  limit?: number
  offset?: number
  filter?: string[]
  sort?: string[]
  facets?: string[]
  categories?: string[]
  minPrice?: number
  maxPrice?: number
  currencyCode?: string
  // Dynamic metadata filters
  brands?: string[]
  mainCategories?: string[]
  subcategories?: string[]
  packaging?: string[]
  orderUnit?: string[]
  ean?: string[]
  region?: string[]
  category?: string[]
  tags?: string[]
  type?: string[]
  // Product attribute filters
  color?: string[]
  voltage?: string[]
  volume?: string[]
  ipProtection?: string[]
  connectivity?: string[]
  gnSize?: string[]
  material?: string[]
  mainMaterial?: string[]
  zmogljivost?: string[]
  proizvodnaZmogljivost?: string[]
  klimatskiRazdred?: string[]
  izdelekZlozljiv?: string[]
  drzavaPorekla?: string[]
  energiskiRazred?: string[]
  primeranZaUporabo?: string[]
  hladilnoSredstvo?: string[]
  odtocnaPipa?: string[]
  steviloVentilatorjev?: string[]
  debelinaRezanja?: string[]
  tezaBaterije?: string[]
  dolzinaKabla?: string[]
  montazoPotrebenElektricar?: string[]
  mocPlinskegaGorilnika?: string[]
  tlakPlina?: string[]
  stopnjaRegulacije?: string[]
  enotaZaRegulacije?: string[]
  zaPriklopVodovodar?: string[]
  osnovniPremer?: string[]
  dolzinaRezila?: string[]
  casPripraveVode?: string[]
  tezaVsebine?: string[]
  brutoTeza?: string[]
  netoTeza?: string[]
  dolzinaRocaja?: string[]
  premer?: string[]
  sirina?: string[]
  dolzina?: string[]
  visina?: string[]
  notranjaSirina?: string[]
  notranjaGlobina?: string[]
  notranjaVisina?: string[]
  vhodnaMoc?: string[]
  izhodnaMoc?: string[]
  najvecjiVolumen?: string[]
  najvecjaSirinaZivila?: string[]
  vrtljajevNaMinuto?: string[]
  dolzinaGredi?: string[]
  debelinaIzolacije?: string[]
  temperaturaOd?: string[]
  temperaturaDo?: string[]
  // New attributes
  debelinaMaterial?: string[]
  odporenNaTempOd?: string[]
  odporenNaTempDo?: string[]
  premerZgoraj?: string[]
  merePredala?: string[]
  dolzinaXSirina?: string[]
  premerSpodaj?: string[]
  sifraIzdelka?: string[]
  zmogljivostCrpalke?: string[]
  vrstaIzdelka?: string[]
  prostorninaVMl?: string[]
  dolzinaRogljev?: string[]
  dolzinaXSirinaXVisina?: string[]
  dolzinaXVisina?: string[]
  zunanjaDelzinaXSirinaXVisina?: string[]
  notranjaDelzinaXSirinaXVisina?: string[]
  velikostZajemalke?: string[]
  motiv?: string[]
  casomer?: string[]
  enotaCasomera?: string[]
  razmikMedVodiliPladnjev?: string[]
  uporabnaProstornina?: string[]
  vrstaKontrolnePlosce?: string[]
  tehtanjeOd?: string[]
  tehtanjeDo?: string[]
  izdelekJePotrebnoSestaviti?: string[]
  vsebujeBaterijo?: string[]
  steviloBaterij?: string[]
  izdelekJeTekoc?: string[]
  brand?: string[]
  /** Restrict results to these Medusa product ids (used for brand-by-handle on category pages). */
  productIds?: string[]
  // Quick filters
  inStock?: boolean
  fastDelivery?: boolean
  bulkPricing?: boolean
  programmable?: boolean
}

export interface SearchResponse {
  hits: any[]
  query: string
  processingTimeMs: number
  limit: number
  offset: number
  estimatedTotalHits: number
  facetDistribution?: Record<string, Record<string, number>>
  facetStats?: Record<string, any>
  /** True when Meilisearch total may be capped (e.g. maxTotalHits); do not treat result_count as exact inventory. */
  totalHitsReportCapped?: boolean
}

/** Map raw Meilisearch search payload to SearchResponse (handles missing estimatedTotalHits / totalHits alias). */
function buildSearchResponseFromMeili(searchResponse: {
  hits?: unknown[]
  query?: string
  processingTimeMs?: number
  limit?: number
  offset?: number
  estimatedTotalHits?: number
  totalHits?: number
  exhaustiveNbHits?: boolean
  facetDistribution?: Record<string, Record<string, number>>
  facetStats?: Record<string, unknown>
}): SearchResponse {
  const hits = (searchResponse.hits ?? []) as any[]
  const pageHits = hits.length
  const offset = typeof searchResponse.offset === "number" ? searchResponse.offset : 0

  const n = (v: unknown) => (typeof v === "number" && !Number.isNaN(v) ? v : undefined)

  const estimatedTotalHits =
    n(searchResponse.estimatedTotalHits) ??
    n(searchResponse.totalHits) ??
    (pageHits > 0 ? offset + pageHits : 0)

  const exhaustive = searchResponse.exhaustiveNbHits === true
  // Meilisearch default maxTotalHits is 1000; when not exhaustive, totals at 1000 are often a cap, not the real catalog size.
  const totalHitsReportCapped = estimatedTotalHits >= 1000 && !exhaustive

  return {
    hits,
    query: searchResponse.query ?? "",
    processingTimeMs: searchResponse.processingTimeMs ?? 0,
    limit: searchResponse.limit ?? 0,
    offset,
    estimatedTotalHits,
    facetDistribution: searchResponse.facetDistribution,
    facetStats: searchResponse.facetStats as Record<string, any> | undefined,
    totalHitsReportCapped,
  }
}

// Define available facets - only include those that are actually configured in MeiliSearch
// Start with basic facets that are likely to be configured
export const AVAILABLE_FACETS = [
  "categories.name",
  "categories.handle", 
  "categories.id",
  "categories.mpath",
  "collection.handle",
  "type.value",
  "tags.value",
  // Krek warehouse specific facets
  "has_krek_stock",
  "fastest_delivery_days"
]

// Extended facets for metadata - these may not be configured yet
export const EXTENDED_FACETS = [
  "material",
  "metadata.EAN",
  "metadata.Zaloga",
  "metadata.Območje",
  "metadata.Material",
  "metadata.Pakiranje",
  "metadata.Kategorija",
  "metadata.Podkategorija",
  "metadata.Država porekla",
  "metadata.Identifikacija",
  "metadata.Blagovna znamka",
  "metadata.Enota naročila",
  "metadata.Glavna kategorija",
  // New facets from filterable attributes
  "metadata.Barva",
  "metadata.Napetost",
  "metadata.Prostornina (l)",
  "metadata.IP zaščita",
  "metadata.Povezljivost",
  "metadata.GN velikost",
  "metadata.Programabilen",
  "metadata.Material",
  "metadata.Glavni material",
  "metadata.Zmogljivost",
  "metadata.Proizvodna zmogljivost",
  "metadata.Klimatski razdred",
  "metadata.Izdelek zložljiv",
  "metadata.Država porekla",
  "metadata.Energiski razred",
  "metadata.Primeran za uporabo",
  "metadata.Hladilno sredstvo",
  "metadata.Odtočna pipa",
  "metadata.Število ventilatorjev",
  "metadata.Debelina rezanja",
  "metadata.Teža baterije",
  "metadata.Dolžina kabla",
  "metadata.Montažo potreben električar ",
  "metadata.Moč plinskega gorilnika",
  "metadata.Tlak plina",
  "metadata.Stopnja regulacije ",
  "metadata.Enota za regulacije",
  "metadata.Za priklop vodovodar",
  "metadata.Osnovni premer (cm)",
  "metadata.Dolžina rezila (cm)",
  "metadata.Čas priprave vode (min)",
  "metadata.Teža vsebine (kg)",
  "metadata.Bruto teža (kg)",
  "metadata.Neto teža (kg)",
  "metadata.Dolžina ročaja (cm)",
  "metadata.Premer (cm)",
  "metadata.Širina (cm)",
  "metadata.Dolžina (cm)",
  "metadata.Višina (cm)",
  "metadata.Notranja širina (cm)",
  "metadata.Notranja globina (cm)",
  "metadata.Notranja višina (cm)",
  "metadata.Vhodna moč (watt)",
  "metadata.Izhodna moč (watt)",
  "metadata.Največji volumen (l)",
  "metadata.Največja širina živila (cm)",
  "metadata.Vrtljajev na minuto",
  "metadata.Dolžina gredi (cm)",
  "metadata.Debelina izolacije (mm)",
  "metadata.Temperatura od",
  "metadata.Temperatura do",
  // New metadata attributes
  "metadata.Debelina material (mm)",
  "metadata.Odporen na temp. od",
  "metadata.Odporen na temp. do",
  "metadata.Premer zgoraj (cm)",
  "metadata.Mere predala dolžina x širina x višina (cm)",
  "metadata.Dolžina x širirna (cm)",
  "metadata.Premer spodaj (cm)",
  "metadata.Šifra izdelka",
  "metadata.Zmogljivost črpalke",
  "metadata.Vrsta izdelka",
  "metadata.Prostornina v ml",
  "metadata.Dolžina rogljev",
  "metadata.Dolžina x širina x višina (cm)",
  "metadata.Dolžina x višina (cm)",
  "metadata.Zunanja dolžina x širirna x višina (cm)",
  "metadata.Notranja dolžina x širina x višina (cm)",
  "metadata.Velikost zajemalke dolžina x širina (cm)",
  "metadata.Motiv",
  "metadata.Časomer",
  "metadata.Enota časomera",
  "metadata.Razmik med vodili pladnjev",
  "metadata.Uporabna prostornina",
  "metadata.Vrsta kontolne plošče",
  "metadata.Tehtanje od",
  "metadata.Tehtanje do",
  "metadata.Izdelek je potrebno sestaviti",
  "metadata.Vsebuje baterijo",
  "metadata.Število baterij",
  "metadata.Izdelek je tekoč",
  "metadata.Brand",
]

// Map user-friendly filter names to Meilisearch filter attributes
export const FILTER_ATTRIBUTE_MAP = {
  categories: "categories.id",
  brands: "metadata.Blagovna znamka",
  subcategories: "metadata.Podkategorija",
  packaging: "metadata.Pakiranje",
  orderUnit: "metadata.Enota naročila",
  ean: "metadata.Ean",
  region: "metadata.Območje",
  category: "metadata.Kategorija",
  tags: "tags.value",
  type: "type.value",
  // New mappings
  color: "metadata.Barva",
  voltage: "metadata.Napetost",
  volume: "metadata.Prostornina (l)",
  ipProtection: "metadata.IP zaščita",
  connectivity: "metadata.Povezljivost",
  gnSize: "metadata.GN velikost",
  material: "metadata.Material",
  mainMaterial: "metadata.Glavni material",
  zmogljivost: "metadata.Zmogljivost",
  proizvodnaZmogljivost: "metadata.Proizvodna zmogljivost",
  klimatskiRazdred: "metadata.Klimatski razdred",
  izdelekZlozljiv: "metadata.Izdelek zložljiv",
  drzavaPorekla: "metadata.Država porekla",
  energiskiRazred: "metadata.Energiski razred",
  primeranZaUporabo: "metadata.Primeran za uporabo",
  hladilnoSredstvo: "metadata.Hladilno sredstvo",
  odtocnaPipa: "metadata.Odtočna pipa",
  steviloVentilatorjev: "metadata.Število ventilatorjev",
  debelinaRezanja: "metadata.Debelina rezanja",
  tezaBaterije: "metadata.Teža baterije",
  dolzinaKabla: "metadata.Dolžina kabla",
  montazoPotrebenElektricar: "metadata.Montažo potreben električar ",
  mocPlinskegaGorilnika: "metadata.Moč plinskega gorilnika",
  tlakPlina: "metadata.Tlak plina",
  stopnjaRegulacije: "metadata.Stopnja regulacije ",
  enotaZaRegulacije: "metadata.Enota za regulacije",
  zaPriklopVodovodar: "metadata.Za priklop vodovodar",
  osnovniPremer: "metadata.Osnovni premer (cm)",
  dolzinaRezila: "metadata.Dolžina rezila (cm)",
  casPripraveVode: "metadata.Čas priprave vode (min)",
  tezaVsebine: "metadata.Teža vsebine (kg)",
  brutoTeza: "metadata.Bruto teža (kg)",
  netoTeza: "metadata.Neto teža (kg)",
  dolzinaRocaja: "metadata.Dolžina ročaja (cm)",
  premer: "metadata.Premer (cm)",
  sirina: "metadata.Širina (cm)",
  dolzina: "metadata.Dolžina (cm)",
  visina: "metadata.Višina (cm)",
  notranjaSirina: "metadata.Notranja širina (cm)",
  notranjaGlobina: "metadata.Notranja globina (cm)",
  notranjaVisina: "metadata.Notranja višina (cm)",
  vhodnaMoc: "metadata.Vhodna moč (watt)",
  izhodnaMoc: "metadata.Izhodna moč (watt)",
  najvecjiVolumen: "metadata.Največji volumen (l)",
  najvecjaSirinaZivila: "metadata.Največja širina živila (cm)",
  vrtljajevNaMinuto: "metadata.Vrtljajev na minuto",
  dolzinaGredi: "metadata.Dolžina gredi (cm)",
  debelinaIzolacije: "metadata.Debelina izolacije (mm)",
  temperaturaOd: "metadata.Temperatura od",
  temperaturaDo: "metadata.Temperatura do",
  // New attribute mappings
  debelinaMaterial: "metadata.Debelina material (mm)",
  odporenNaTempOd: "metadata.Odporen na temp. od",
  odporenNaTempDo: "metadata.Odporen na temp. do",
  premerZgoraj: "metadata.Premer zgoraj (cm)",
  merePredala: "metadata.Mere predala dolžina x širina x višina (cm)",
  dolzinaXSirina: "metadata.Dolžina x širirna (cm)",
  premerSpodaj: "metadata.Premer spodaj (cm)",
  sifraIzdelka: "metadata.Šifra izdelka",
  zmogljivostCrpalke: "metadata.Zmogljivost črpalke",
  vrstaIzdelka: "metadata.Vrsta izdelka",
  prostorninaVMl: "metadata.Prostornina v ml",
  dolzinaRogljev: "metadata.Dolžina rogljev",
  dolzinaXSirinaXVisina: "metadata.Dolžina x širina x višina (cm)",
  dolzinaXVisina: "metadata.Dolžina x višina (cm)",
  zunanjaDelzinaXSirinaXVisina: "metadata.Zunanja dolžina x širirna x višina (cm)",
  notranjaDelzinaXSirinaXVisina: "metadata.Notranja dolžina x širina x višina (cm)",
  velikostZajemalke: "metadata.Velikost zajemalke dolžina x širina (cm)",
  motiv: "metadata.Motiv",
  casomer: "metadata.Časomer",
  enotaCasomera: "metadata.Enota časomera",
  razmikMedVodiliPladnjev: "metadata.Razmik med vodili pladnjev",
  uporabnaProstornina: "metadata.Uporabna prostornina",
  vrstaKontrolnePlosce: "metadata.Vrsta kontolne plošče",
  tehtanjeOd: "metadata.Tehtanje od",
  tehtanjeDo: "metadata.Tehtanje do",
  izdelekJePotrebnoSestaviti: "metadata.Izdelek je potrebno sestaviti",
  vsebujeBaterijo: "metadata.Vsebuje baterijo",
  steviloBaterij: "metadata.Število baterij",
  izdelekJeTekoc: "metadata.Izdelek je tekoč",
  brand: "metadata.Brand",
} as const

const CLIENT_SAFE_FILTERABLE_FACETS = [
  ...new Set([
    ...AVAILABLE_FACETS,
    ...Object.values(FILTER_ATTRIBUTE_MAP).filter(
      (facet) =>
        ![
          "metadata.Podkategorija",
          "metadata.Pakiranje",
          "metadata.Enota naročila",
          "metadata.Območje",
          "metadata.Kategorija",
        ].includes(facet)
    ),
    "categories.name",
  ]),
]

/**
 * Build Meilisearch filter strings from search parameters
 */
export const buildMeilisearchFilters = (params: SearchParams): string[] => {
  const filters: string[] = []

  // Category filters
  if (params.categories && params.categories.length > 0) {
    const categoryFilter = params.categories.map(catId => `"${catId}"`).join(", ")
    filters.push(`categories.id IN [${categoryFilter}]`)
  }

  // Product id filter (brand handle resolved via Medusa — avoids metadata/entity name drift)
  if (params.productIds && params.productIds.length > 0) {
    const idFilter = params.productIds.map((id) => `"${id}"`).join(", ")
    filters.push(`id IN [${idFilter}]`)
  }

  // Brand filters (from metadata) - Use single quotes for attributes with spaces
  if (params.brands && params.brands.length > 0) {
    const brandFilter = params.brands
      .map((brand) => brand.trim())
      .filter(Boolean)
      .map((brand) => `"${brand}"`)
      .join(", ")
    if (brandFilter) {
      filters.push(`'metadata.Blagovna znamka' IN [${brandFilter}]`)
    }
  }

  // Subcategory filters (from metadata) - Use single quotes for attributes with spaces
  if (params.subcategories && params.subcategories.length > 0) {
    const subcategoryFilter = params.subcategories
      .map((subcat) => `"${subcat}"`)
      .join(", ")
    filters.push(`'metadata.Podkategorija' IN [${subcategoryFilter}]`)
  }

  // Packaging filters
  if (params.packaging && params.packaging.length > 0) {
    const packagingFilter = params.packaging.map((p) => `"${p}"`).join(", ")
    filters.push(`'metadata.Pakiranje' IN [${packagingFilter}]`)
  }

  // Order unit filters
  if (params.orderUnit && params.orderUnit.length > 0) {
    const orderUnitFilter = params.orderUnit.map((ou) => `"${ou}"`).join(", ")
    filters.push(`'metadata.Enota naročila' IN [${orderUnitFilter}]`)
  }

  // EAN filters
  if (params.ean && params.ean.length > 0) {
    const eanFilter = params.ean.map((e) => `"${e}"`).join(", ")
    filters.push(`'metadata.Ean' IN [${eanFilter}]`)
  }

  // Region filters
  if (params.region && params.region.length > 0) {
    const regionFilter = params.region.map((r) => `"${r}"`).join(", ")
    filters.push(`'metadata.Območje' IN [${regionFilter}]`)
  }

  // Category filters
  if (params.category && params.category.length > 0) {
    const categoryFilter = params.category.map((c) => `"${c}"`).join(", ")
    filters.push(`'metadata.Kategorija' IN [${categoryFilter}]`)
  }

  // Tags filters
  if (params.tags && params.tags.length > 0) {
    const tagsFilter = params.tags.map(tag => `"${tag}"`).join(", ")
    filters.push(`'tags.value' IN [${tagsFilter}]`)
  }

  // Type filters
  if (params.type && params.type.length > 0) {
    const typeFilter = params.type.map(type => `"${type}"`).join(", ")
    filters.push(`'type.value' IN [${typeFilter}]`)
  }

  // New filters
  if (params.color && params.color.length > 0) {
    const colorFilter = params.color.map(c => `"${c}"`).join(", ")
    filters.push(`'metadata.Barva' IN [${colorFilter}]`)
  }

  if (params.voltage && params.voltage.length > 0) {
    const voltageFilter = params.voltage.map((v) => `"${v}"`).join(", ")
    filters.push(`'metadata.Napetost' IN [${voltageFilter}]`)
  }

  if (params.vhodnaMoc && params.vhodnaMoc.length > 0) {
    const vhodnaMocFilter = params.vhodnaMoc.map((ip) => `"${ip}"`).join(", ")
    filters.push(`'metadata.Vhodna moč (watt)' IN [${vhodnaMocFilter}]`)
  }

  if (params.volume && params.volume.length > 0) {
    const volumeFilter = params.volume.map((v) => `"${v}"`).join(", ")
    filters.push(`'metadata.Prostornina (l)' IN [${volumeFilter}]`)
  }

  if (params.ipProtection && params.ipProtection.length > 0) {
    const ipProtectionFilter = params.ipProtection.map(ip => `"${ip}"`).join(", ")
    filters.push(`'metadata.IP zaščita' IN [${ipProtectionFilter}]`)
  }

  if (params.connectivity && params.connectivity.length > 0) {
    const connectivityFilter = params.connectivity.map(c => `"${c}"`).join(", ")
    filters.push(`'metadata.Povezljivost' IN [${connectivityFilter}]`)
  }

  if (params.gnSize && params.gnSize.length > 0) {
    const gnSizeFilter = params.gnSize.map(gn => `"${gn}"`).join(", ")
    filters.push(`'metadata.GN velikost' IN [${gnSizeFilter}]`)
  }

  if (params.material && params.material.length > 0) {
    const materialFilter = params.material.map(m => `"${m}"`).join(", ")
    filters.push(`'metadata.Material' IN [${materialFilter}]`)
  }

  if (params.mainMaterial && params.mainMaterial.length > 0) {
    const mainMaterialFilter = params.mainMaterial
      .map((mm: string) => `"${mm}"`)
      .join(", ")
    filters.push(`'metadata.Glavni material' IN [${mainMaterialFilter}]`)
  }

  if (params.zmogljivost && params.zmogljivost.length > 0) {
    const zmogljivostFilter = params.zmogljivost.map(z => `"${z}"`).join(", ")
    filters.push(`'metadata.Zmogljivost' IN [${zmogljivostFilter}]`)
  }

  if (params.proizvodnaZmogljivost && params.proizvodnaZmogljivost.length > 0) {
    const proizvodnaZmogljivostFilter = params.proizvodnaZmogljivost.map(pz => `"${pz}"`).join(", ")
    filters.push(`'metadata.Proizvodna zmogljivost' IN [${proizvodnaZmogljivostFilter}]`)
  }

  if (params.klimatskiRazdred && params.klimatskiRazdred.length > 0) {
    const klimatskiRazdredFilter = params.klimatskiRazdred.map(kr => `"${kr}"`).join(", ")
    filters.push(`'metadata.Klimatski razdred' IN [${klimatskiRazdredFilter}]`)
  }

  if (params.izdelekZlozljiv && params.izdelekZlozljiv.length > 0) {
    const izdelekZlozljivFilter = params.izdelekZlozljiv.map(iz => `"${iz}"`).join(", ")
    filters.push(`'metadata.Izdelek zložljiv' IN [${izdelekZlozljivFilter}]`)
  }

  if (params.drzavaPorekla && params.drzavaPorekla.length > 0) {
    const drzavaPoreklaFilter = params.drzavaPorekla.map(dp => `"${dp}"`).join(", ")
    filters.push(`'metadata.Država porekla' IN [${drzavaPoreklaFilter}]`)
  }

  if (params.energiskiRazred && params.energiskiRazred.length > 0) {
    const energiskiRazredFilter = params.energiskiRazred.map(er => `"${er}"`).join(", ")
    filters.push(`'metadata.Energiski razred' IN [${energiskiRazredFilter}]`)
  }

  if (params.primeranZaUporabo && params.primeranZaUporabo.length > 0) {
    const primeranZaUporaboFilter = params.primeranZaUporabo.map(pzu => `"${pzu}"`).join(", ")
    filters.push(`'metadata.Primeran za uporabo' IN [${primeranZaUporaboFilter}]`)
  }

  if (params.hladilnoSredstvo && params.hladilnoSredstvo.length > 0) {
    const hladilnoSredstvoFilter = params.hladilnoSredstvo.map(hs => `"${hs}"`).join(", ")
    filters.push(`'metadata.Hladilno sredstvo' IN [${hladilnoSredstvoFilter}]`)
  }

  if (params.odtocnaPipa && params.odtocnaPipa.length > 0) {
    const odtocnaPipaFilter = params.odtocnaPipa.map(op => `"${op}"`).join(", ")
    filters.push(`'metadata.Odtočna pipa' IN [${odtocnaPipaFilter}]`)
  }

  if (params.steviloVentilatorjev && params.steviloVentilatorjev.length > 0) {
    const steviloVentilatorjevFilter = params.steviloVentilatorjev.map(sv => `"${sv}"`).join(", ")
    filters.push(`'metadata.Število ventilatorjev' IN [${steviloVentilatorjevFilter}]`)
  }

  if (params.debelinaRezanja && params.debelinaRezanja.length > 0) {
    const debelinaRezanjaFilter = params.debelinaRezanja.map(dr => `"${dr}"`).join(", ")
    filters.push(`'metadata.Debelina rezanja' IN [${debelinaRezanjaFilter}]`)
  }

  if (params.tezaBaterije && params.tezaBaterije.length > 0) {
    const tezaBaterijeFilter = params.tezaBaterije.map(tb => `"${tb}"`).join(", ")
    filters.push(`'metadata.Teža baterije' IN [${tezaBaterijeFilter}]`)
  }

  if (params.dolzinaKabla && params.dolzinaKabla.length > 0) {
    const dolzinaKablaFilter = params.dolzinaKabla.map(dk => `"${dk}"`).join(", ")
    filters.push(`'metadata.Dolžina kabla' IN [${dolzinaKablaFilter}]`)
  }

  if (params.montazoPotrebenElektricar && params.montazoPotrebenElektricar.length > 0) {
    const montazoPotrebenElektricarFilter = params.montazoPotrebenElektricar.map(mpe => `"${mpe}"`).join(", ")
    filters.push(`'metadata.Montažo potreben električar ' IN [${montazoPotrebenElektricarFilter}]`)
  }

  if (params.mocPlinskegaGorilnika && params.mocPlinskegaGorilnika.length > 0) {
    const mocPlinskegaGorilnikaFilter = params.mocPlinskegaGorilnika.map(mpg => `"${mpg}"`).join(", ")
    filters.push(`'metadata.Moč plinskega gorilnika' IN [${mocPlinskegaGorilnikaFilter}]`)
  }

  if (params.tlakPlina && params.tlakPlina.length > 0) {
    const tlakPlinaFilter = params.tlakPlina.map(tp => `"${tp}"`).join(", ")
    filters.push(`'metadata.Tlak plina' IN [${tlakPlinaFilter}]`)
  }

  if (params.stopnjaRegulacije && params.stopnjaRegulacije.length > 0) {
    const stopnjaRegulacijeFilter = params.stopnjaRegulacije.map(sr => `"${sr}"`).join(", ")
    filters.push(`'metadata.Stopnja regulacije ' IN [${stopnjaRegulacijeFilter}]`)
  }

  if (params.enotaZaRegulacije && params.enotaZaRegulacije.length > 0) {
    const enotaZaRegulacijeFilter = params.enotaZaRegulacije.map(ezr => `"${ezr}"`).join(", ")
    filters.push(`'metadata.Enota za regulacije' IN [${enotaZaRegulacijeFilter}]`)
  }

  if (params.zaPriklopVodovodar && params.zaPriklopVodovodar.length > 0) {
    const zaPriklopVodovodarFilter = params.zaPriklopVodovodar.map(zpv => `"${zpv}"`).join(", ")
    filters.push(`'metadata.Za priklop vodovodar' IN [${zaPriklopVodovodarFilter}]`)
  }

  if (params.osnovniPremer && params.osnovniPremer.length > 0) {
    const osnovniPremerFilter = params.osnovniPremer.map(op => `"${op}"`).join(", ")
    filters.push(`'metadata.Osnovni premer (cm)' IN [${osnovniPremerFilter}]`)
  }

  if (params.dolzinaRezila && params.dolzinaRezila.length > 0) {
    const dolzinaRezilaFilter = params.dolzinaRezila.map(dr => `"${dr}"`).join(", ")
    filters.push(`'metadata.Dolžina rezila (cm)' IN [${dolzinaRezilaFilter}]`)
  }

  if (params.casPripraveVode && params.casPripraveVode.length > 0) {
    const casPripraveVodeFilter = params.casPripraveVode.map(cpv => `"${cpv}"`).join(", ")
    filters.push(`'metadata.Čas priprave vode (min)' IN [${casPripraveVodeFilter}]`)
  }

  if (params.tezaVsebine && params.tezaVsebine.length > 0) {
    const tezaVsebineFilter = params.tezaVsebine.map(tv => `"${tv}"`).join(", ")
    filters.push(`'metadata.Teža vsebine (kg)' IN [${tezaVsebineFilter}]`)
  }

  if (params.brutoTeza && params.brutoTeza.length > 0) {
    const brutoTezaFilter = params.brutoTeza.map(bt => `"${bt}"`).join(", ")
    filters.push(`'metadata.Bruto teža (kg)' IN [${brutoTezaFilter}]`)
  }

  if (params.netoTeza && params.netoTeza.length > 0) {
    const netoTezaFilter = params.netoTeza.map(nt => `"${nt}"`).join(", ")
    filters.push(`'metadata.Neto teža (kg)' IN [${netoTezaFilter}]`)
  }

  if (params.dolzinaRocaja && params.dolzinaRocaja.length > 0) {
    const dolzinaRocajaFilter = params.dolzinaRocaja.map(dr => `"${dr}"`).join(", ")
    filters.push(`'metadata.Dolžina ročaja (cm)' IN [${dolzinaRocajaFilter}]`)
  }

  if (params.premer && params.premer.length > 0) {
    const premerFilter = params.premer.map(p => `"${p}"`).join(", ")
    filters.push(`'metadata.Premer (cm)' IN [${premerFilter}]`)
  }

  if (params.sirina && params.sirina.length > 0) {
    const sirinaFilter = params.sirina.map(s => `"${s}"`).join(", ")
    filters.push(`'metadata.Širina (cm)' IN [${sirinaFilter}]`)
  }

  if (params.dolzina && params.dolzina.length > 0) {
    const dolzinaFilter = params.dolzina.map(d => `"${d}"`).join(", ")
    filters.push(`'metadata.Dolžina (cm)' IN [${dolzinaFilter}]`)
  }

  if (params.visina && params.visina.length > 0) {
    const visinaFilter = params.visina.map(v => `"${v}"`).join(", ")
    filters.push(`'metadata.Višina (cm)' IN [${visinaFilter}]`)
  }

  if (params.notranjaSirina && params.notranjaSirina.length > 0) {
    const notranjaSirinaFilter = params.notranjaSirina.map(ns => `"${ns}"`).join(", ")
    filters.push(`'metadata.Notranja širina (cm)' IN [${notranjaSirinaFilter}]`)
  }

  if (params.notranjaGlobina && params.notranjaGlobina.length > 0) {
    const notranjaGlobinaFilter = params.notranjaGlobina.map(ng => `"${ng}"`).join(", ")
    filters.push(`'metadata.Notranja globina (cm)' IN [${notranjaGlobinaFilter}]`)
  }

  if (params.notranjaVisina && params.notranjaVisina.length > 0) {
    const notranjaVisinaFilter = params.notranjaVisina.map(nv => `"${nv}"`).join(", ")
    filters.push(`'metadata.Notranja višina (cm)' IN [${notranjaVisinaFilter}]`)
  }

  if (params.izhodnaMoc && params.izhodnaMoc.length > 0) {
    const izhodnaMocFilter = params.izhodnaMoc.map(im => `"${im}"`).join(", ")
    filters.push(`'metadata.Izhodna moč (watt)' IN [${izhodnaMocFilter}]`)
  }

  if (params.najvecjiVolumen && params.najvecjiVolumen.length > 0) {
    const najvecjiVolumenFilter = params.najvecjiVolumen.map(nv => `"${nv}"`).join(", ")
    filters.push(`'metadata.Največji volumen (l)' IN [${najvecjiVolumenFilter}]`)
  }

  if (params.najvecjaSirinaZivila && params.najvecjaSirinaZivila.length > 0) {
    const najvecjaSirinaZivilaFilter = params.najvecjaSirinaZivila.map(nsz => `"${nsz}"`).join(", ")
    filters.push(`'metadata.Največja širina živila (cm)' IN [${najvecjaSirinaZivilaFilter}]`)
  }

  if (params.vrtljajevNaMinuto && params.vrtljajevNaMinuto.length > 0) {
    const vrtljajevNaMinutoFilter = params.vrtljajevNaMinuto.map(vnm => `"${vnm}"`).join(", ")
    filters.push(`'metadata.Vrtljajev na minuto' IN [${vrtljajevNaMinutoFilter}]`)
  }

  if (params.dolzinaGredi && params.dolzinaGredi.length > 0) {
    const dolzinaGrediFilter = params.dolzinaGredi.map(dg => `"${dg}"`).join(", ")
    filters.push(`'metadata.Dolžina gredi (cm)' IN [${dolzinaGrediFilter}]`)
  }

  if (params.debelinaIzolacije && params.debelinaIzolacije.length > 0) {
    const debelinaIzolacijeFilter = params.debelinaIzolacije.map(di => `"${di}"`).join(", ")
    filters.push(`'metadata.Debelina izolacije (mm)' IN [${debelinaIzolacijeFilter}]`)
  }

  if (params.temperaturaOd && params.temperaturaOd.length > 0) {
    const temperaturaOdFilter = params.temperaturaOd.map(to => `"${to}"`).join(", ")
    filters.push(`'metadata.Temperatura od' IN [${temperaturaOdFilter}]`)
  }

  if (params.temperaturaDo && params.temperaturaDo.length > 0) {
    const temperaturaDoFilter = params.temperaturaDo.map(td => `"${td}"`).join(", ")
    filters.push(`'metadata.Temperatura do' IN [${temperaturaDoFilter}]`)
  }

  // New attribute filters
  if (params.debelinaMaterial && params.debelinaMaterial.length > 0) {
    const debelinaMaterialFilter = params.debelinaMaterial.map(dm => `"${dm}"`).join(", ")
    filters.push(`'metadata.Debelina material (mm)' IN [${debelinaMaterialFilter}]`)
  }

  if (params.odporenNaTempOd && params.odporenNaTempOd.length > 0) {
    const odporenNaTempOdFilter = params.odporenNaTempOd.map(oto => `"${oto}"`).join(", ")
    filters.push(`'metadata.Odporen na temp. od' IN [${odporenNaTempOdFilter}]`)
  }

  if (params.odporenNaTempDo && params.odporenNaTempDo.length > 0) {
    const odporenNaTempDoFilter = params.odporenNaTempDo.map(otd => `"${otd}"`).join(", ")
    filters.push(`'metadata.Odporen na temp. do' IN [${odporenNaTempDoFilter}]`)
  }

  if (params.premerZgoraj && params.premerZgoraj.length > 0) {
    const premerZgorajFilter = params.premerZgoraj.map(pz => `"${pz}"`).join(", ")
    filters.push(`'metadata.Premer zgoraj (cm)' IN [${premerZgorajFilter}]`)
  }

  if (params.merePredala && params.merePredala.length > 0) {
    const merePredalFilter = params.merePredala.map(mp => `"${mp}"`).join(", ")
    filters.push(`'metadata.Mere predala dolžina x širina x višina (cm)' IN [${merePredalFilter}]`)
  }

  if (params.dolzinaXSirina && params.dolzinaXSirina.length > 0) {
    const dolzinaXSirinaFilter = params.dolzinaXSirina.map(dxs => `"${dxs}"`).join(", ")
    filters.push(`'metadata.Dolžina x širirna (cm)' IN [${dolzinaXSirinaFilter}]`)
  }

  if (params.premerSpodaj && params.premerSpodaj.length > 0) {
    const premerSpodajFilter = params.premerSpodaj.map(ps => `"${ps}"`).join(", ")
    filters.push(`'metadata.Premer spodaj (cm)' IN [${premerSpodajFilter}]`)
  }

  if (params.sifraIzdelka && params.sifraIzdelka.length > 0) {
    const sifraIzdelkaFilter = params.sifraIzdelka.map(si => `"${si}"`).join(", ")
    filters.push(`'metadata.Šifra izdelka' IN [${sifraIzdelkaFilter}]`)
  }

  if (params.zmogljivostCrpalke && params.zmogljivostCrpalke.length > 0) {
    const zmogljivostCrpalkeFilter = params.zmogljivostCrpalke.map(zc => `"${zc}"`).join(", ")
    filters.push(`'metadata.Zmogljivost črpalke' IN [${zmogljivostCrpalkeFilter}]`)
  }

  if (params.vrstaIzdelka && params.vrstaIzdelka.length > 0) {
    const vrstaIzdelkaFilter = params.vrstaIzdelka.map(vi => `"${vi}"`).join(", ")
    filters.push(`'metadata.Vrsta izdelka' IN [${vrstaIzdelkaFilter}]`)
  }

  if (params.prostorninaVMl && params.prostorninaVMl.length > 0) {
    const prostorninaVMlFilter = params.prostorninaVMl.map(pvm => `"${pvm}"`).join(", ")
    filters.push(`'metadata.Prostornina v ml' IN [${prostorninaVMlFilter}]`)
  }

  if (params.dolzinaRogljev && params.dolzinaRogljev.length > 0) {
    const dolzinaRogijevFilter = params.dolzinaRogljev.map(dr => `"${dr}"`).join(", ")
    filters.push(`'metadata.Dolžina rogljev' IN [${dolzinaRogijevFilter}]`)
  }

  if (params.dolzinaXSirinaXVisina && params.dolzinaXSirinaXVisina.length > 0) {
    const dolzinaXSirinaXVisinaFilter = params.dolzinaXSirinaXVisina.map(dxsxv => `"${dxsxv}"`).join(", ")
    filters.push(`'metadata.Dolžina x širina x višina (cm)' IN [${dolzinaXSirinaXVisinaFilter}]`)
  }

  if (params.dolzinaXVisina && params.dolzinaXVisina.length > 0) {
    const dolzinaXVisinaFilter = params.dolzinaXVisina.map(dxv => `"${dxv}"`).join(", ")
    filters.push(`'metadata.Dolžina x višina (cm)' IN [${dolzinaXVisinaFilter}]`)
  }

  if (params.zunanjaDelzinaXSirinaXVisina && params.zunanjaDelzinaXSirinaXVisina.length > 0) {
    const zunanjaDelzinaXSirinaXVisinaFilter = params.zunanjaDelzinaXSirinaXVisina.map(zdxsxv => `"${zdxsxv}"`).join(", ")
    filters.push(`'metadata.Zunanja dolžina x širirna x višina (cm)' IN [${zunanjaDelzinaXSirinaXVisinaFilter}]`)
  }

  if (params.notranjaDelzinaXSirinaXVisina && params.notranjaDelzinaXSirinaXVisina.length > 0) {
    const notranjaDelzinaXSirinaXVisinaFilter = params.notranjaDelzinaXSirinaXVisina.map(ndxsxv => `"${ndxsxv}"`).join(", ")
    filters.push(`'metadata.Notranja dolžina x širina x višina (cm)' IN [${notranjaDelzinaXSirinaXVisinaFilter}]`)
  }

  if (params.velikostZajemalke && params.velikostZajemalke.length > 0) {
    const velikostZajemalkeFilter = params.velikostZajemalke.map(vz => `"${vz}"`).join(", ")
    filters.push(`'metadata.Velikost zajemalke dolžina x širina (cm)' IN [${velikostZajemalkeFilter}]`)
  }

  if (params.motiv && params.motiv.length > 0) {
    const motivFilter = params.motiv.map(m => `"${m}"`).join(", ")
    filters.push(`'metadata.Motiv' IN [${motivFilter}]`)
  }

  if (params.casomer && params.casomer.length > 0) {
    const casomerFilter = params.casomer.map(c => `"${c}"`).join(", ")
    filters.push(`'metadata.Časomer' IN [${casomerFilter}]`)
  }

  if (params.enotaCasomera && params.enotaCasomera.length > 0) {
    const enotaCasomeraFilter = params.enotaCasomera.map(ec => `"${ec}"`).join(", ")
    filters.push(`'metadata.Enota časomera' IN [${enotaCasomeraFilter}]`)
  }

  if (params.razmikMedVodiliPladnjev && params.razmikMedVodiliPladnjev.length > 0) {
    const razmikMedVodiliPladnjevFilter = params.razmikMedVodiliPladnjev.map(rmvp => `"${rmvp}"`).join(", ")
    filters.push(`'metadata.Razmik med vodili pladnjev' IN [${razmikMedVodiliPladnjevFilter}]`)
  }

  if (params.uporabnaProstornina && params.uporabnaProstornina.length > 0) {
    const uporabnaProstorninaFilter = params.uporabnaProstornina.map(up => `"${up}"`).join(", ")
    filters.push(`'metadata.Uporabna prostornina' IN [${uporabnaProstorninaFilter}]`)
  }

  if (params.vrstaKontrolnePlosce && params.vrstaKontrolnePlosce.length > 0) {
    const vrstaKontrolnePlosceFilter = params.vrstaKontrolnePlosce.map(vkp => `"${vkp}"`).join(", ")
    filters.push(`'metadata.Vrsta kontolne plošče' IN [${vrstaKontrolnePlosceFilter}]`)
  }

  if (params.tehtanjeOd && params.tehtanjeOd.length > 0) {
    const tehtanjeOdFilter = params.tehtanjeOd.map(to => `"${to}"`).join(", ")
    filters.push(`'metadata.Tehtanje od' IN [${tehtanjeOdFilter}]`)
  }

  if (params.tehtanjeDo && params.tehtanjeDo.length > 0) {
    const tehtanjeDoFilter = params.tehtanjeDo.map(td => `"${td}"`).join(", ")
    filters.push(`'metadata.Tehtanje do' IN [${tehtanjeDoFilter}]`)
  }

  if (params.izdelekJePotrebnoSestaviti && params.izdelekJePotrebnoSestaviti.length > 0) {
    const izdelekJePotrebnoSestavitiFilter = params.izdelekJePotrebnoSestaviti.map(ijps => `"${ijps}"`).join(", ")
    filters.push(`'metadata.Izdelek je potrebno sestaviti' IN [${izdelekJePotrebnoSestavitiFilter}]`)
  }

  if (params.vsebujeBaterijo && params.vsebujeBaterijo.length > 0) {
    const vsebujeBaterijoFilter = params.vsebujeBaterijo.map(vb => `"${vb}"`).join(", ")
    filters.push(`'metadata.Vsebuje baterijo' IN [${vsebujeBaterijoFilter}]`)
  }

  if (params.steviloBaterij && params.steviloBaterij.length > 0) {
    const steviloBaterijFilter = params.steviloBaterij.map(sb => `"${sb}"`).join(", ")
    filters.push(`'metadata.Število baterij' IN [${steviloBaterijFilter}]`)
  }

  if (params.izdelekJeTekoc && params.izdelekJeTekoc.length > 0) {
    const izdelekJeTekocFilter = params.izdelekJeTekoc.map(ijt => `"${ijt}"`).join(", ")
    filters.push(`'metadata.Izdelek je tekoč' IN [${izdelekJeTekocFilter}]`)
  }

  if (params.brand && params.brand.length > 0) {
    const brandFilter = params.brand.map(b => `"${b}"`).join(", ")
    filters.push(`'metadata.Brand' IN [${brandFilter}]`)
  }

  // Price range filters - filter by the actual discounted prices array
  if (params.minPrice !== undefined && params.minPrice > 0) {
    filters.push(`prices >= ${params.minPrice}`)
  }
  
  if (params.maxPrice !== undefined && params.maxPrice < 10000) {
    filters.push(`prices <= ${params.maxPrice}`)
  }

  // Stock filters
  if (params.inStock) {
    // Filter for products that have available inventory
    // Use the filterable attributes that are actually available in MeiliSearch
    filters.push(`total_available_quantity > 0`)
  }

  // Fast delivery filter - filter for products with Krek stock
  if (params.fastDelivery) {
    filters.push(`has_krek_stock = true`)
  }

  if (params.bulkPricing) {
    // Example: assuming bulk pricing is stored in metadata  
    filters.push(`metadata.bulk_pricing = true`)
  }
  
  if (params.programmable) {
    filters.push(`metadata.Programabilen = true`)
  }

  // Additional filters from search params
  if (params.filter && params.filter.length > 0) {
    filters.push(...params.filter)
  }

  return filters
}

/**
 * Parse price from metadata string (e.g., "€ 24,50" -> 24.50)
 */
export const parsePrice = (priceString: string): number => {
  if (!priceString) return 0
  
  // Remove currency symbols and parse
  const cleanPrice = priceString.replace(/[€$£¥₹]/g, '').replace(/,/g, '.').trim()
  const parsed = parseFloat(cleanPrice)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Get price range from search results
 */
export const getPriceRange = (hits: any[]): { min: number; max: number } => {
  let min = Infinity
  let max = -Infinity

  hits.forEach(hit => {
    // Use the prices array which contains the actual discounted prices
    if (hit.prices && Array.isArray(hit.prices) && hit.prices.length > 0) {
      hit.prices.forEach((price: number) => {
        if (price && price > 0) {
          min = Math.min(min, price)
          max = Math.max(max, price)
        }
      })
    }

    // Fallback to metadata price if prices array is not available
    if (hit.metadata && hit.metadata["Originalna cena"]) {
      const price = parsePrice(hit.metadata["Originalna cena"])
      if (price > 0) {
        min = Math.min(min, price)
        max = Math.max(max, price)
      }
    }
  })

  return {
    min: min === Infinity ? 0 : min,
    max: max === -Infinity ? 1000 : max
  }
}

/**
 * Perform a search for products in Meilisearch
 */
export const searchProducts = async (params: SearchParams): Promise<SearchResponse> => {
  const {
    q = "",
    limit = 20,
    offset = 0,
    sort = [],
    facets,
    ...otherParams
  } = params

  try {
    // Build filters
    const filters = buildMeilisearchFilters(otherParams)
    const filterString = filters.length > 0 ? filters.join(" AND ") : undefined

    // Get contextually relevant facets instead of requesting all facets
    const contextualFacets = facets || getContextualFacets(params, false)
    
    // Client-side search uses a public search key, which can search but cannot read index settings.
    // Use a frontend-safe allowlist instead of calling `index.getSettings()`.
    let availableFacets = contextualFacets.filter((facet) =>
      CLIENT_SAFE_FILTERABLE_FACETS.includes(facet)
    )
    // Fallback: if filtering removed all facets (e.g. production index has different attribute names),
    // request core facets so we still get facetDistribution for dynamic filters
    const CORE_FACETS_FALLBACK = [
      "categories.id",
      "categories.name",
      "metadata.Blagovna znamka",
      "metadata.Material",
      "metadata.Glavni material",
      "metadata.Barva",
      "metadata.Država porekla",
    ]
    if (availableFacets.length === 0) {
      availableFacets = CORE_FACETS_FALLBACK
    }

    // Try search with available facets
    let searchResponse
    try {
      searchResponse = await unifiedClient.index(UNIFIED_PRODUCTS_INDEX).search(q, {
        limit,
        offset,
        filter: filterString,
        facets: availableFacets,
        sort: sort.length > 0 ? sort : undefined,
        matchingStrategy: "last",
      })
    } catch (facetError) {
      // Retry without facets if facet configuration is invalid
      searchResponse = await unifiedClient.index(UNIFIED_PRODUCTS_INDEX).search(q, {
        limit,
        offset,
        filter: filterString,
        sort: sort.length > 0 ? sort : undefined,
        matchingStrategy: "last",
      })
    }



    return buildSearchResponseFromMeili(searchResponse)
  } catch (error) {
    throw error
  }
}

export const searchProductsB2C = async (params: SearchParams): Promise<SearchResponse> => {
  const {
    q = "",
    limit = 20,
    offset = 0,
    sort = [],
    facets,
    ...otherParams
  } = params

  try {
    const filters = buildMeilisearchFilters(otherParams)
    const filterString = filters.length > 0 ? filters.join(" AND ") : undefined

    const contextualFacets = facets || getContextualFacets(params, false)
    let availableFacets = contextualFacets.filter((facet) =>
      CLIENT_SAFE_FILTERABLE_FACETS.includes(facet)
    )

    const CORE_FACETS_FALLBACK = [
      "categories.id",
      "categories.name",
      "metadata.Blagovna znamka",
      "metadata.Material",
      "metadata.Glavni material",
      "metadata.Barva",
      "metadata.Država porekla",
    ]

    if (availableFacets.length === 0) {
      availableFacets = CORE_FACETS_FALLBACK
    }

    let searchResponse
    try {
      searchResponse = await client.index(B2C_PRODUCTS_INDEX).search(q, {
        limit,
        offset,
        filter: filterString,
        facets: availableFacets,
        sort: sort.length > 0 ? sort : undefined,
        matchingStrategy: "last",
      })
    } catch {
      searchResponse = await client.index(B2C_PRODUCTS_INDEX).search(q, {
        limit,
        offset,
        filter: filterString,
        sort: sort.length > 0 ? sort : undefined,
        matchingStrategy: "last",
      })
    }

    return buildSearchResponseFromMeili(searchResponse)
  } catch (error) {
    throw error
  }
}

/**
 * Wholesale shop products for the /search wholesale blocks, straight from the
 * B2B Meilisearch `products` index (not products_unified).
 * Sidebar filters apply to trgovina results only, so this matches on the query alone.
 */
export const searchB2BOnlyProducts = async ({
  q = "",
  limit = 24,
  offset = 0,
  sort = [],
}: Pick<SearchParams, "q" | "limit" | "offset" | "sort">): Promise<SearchResponse> => {
  const index = unifiedClient.index(B2B_PRODUCTS_INDEX)
  const base = { limit, offset, matchingStrategy: "last" as const }

  let searchResponse
  try {
    searchResponse = await index.search(q, {
      ...base,
      sort: sort.length > 0 ? sort : undefined,
    })
  } catch {
    // The B2B index may not have the trgovina sort attributes configured
    searchResponse = await index.search(q, base)
  }

  const response = buildSearchResponseFromMeili(searchResponse)
  // Raw B2B docs carry no exists_on_trgovina flag; the cards use it to link to horeca.krek.si
  return {
    ...response,
    hits: response.hits.map((hit) => ({ ...hit, exists_on_trgovina: false })),
  }
}

export interface SearchCategoryHit {
  id: string
  name: string
  handle: string
}

export interface SearchCollectionHit {
  id: string
  title: string
  handle: string
}

export interface SearchTaxonomyResult {
  categories: SearchCategoryHit[]
  collections: SearchCollectionHit[]
}

/**
 * Categories and collections matching the query (B2C Meilisearch), minus
 * collections the storefront hides (excluded or without products).
 */
export const searchTaxonomy = async (q: string, limit = 12): Promise<SearchTaxonomyResult> => {
  const { results } = await client.multiSearch({
    queries: [
      { indexUid: "categories", q, limit, attributesToRetrieve: ["id", "name", "handle"] },
      { indexUid: "collections", q, limit, attributesToRetrieve: ["id", "title", "handle"] },
    ],
  })

  const categories = (results[0]?.hits ?? []) as unknown as SearchCategoryHit[]
  const collectionHits = (results[1]?.hits ?? []) as unknown as SearchCollectionHit[]

  if (collectionHits.length === 0) {
    return { categories, collections: [] }
  }

  // Fail open like listCollections: if the visibility check errors, keep every hit.
  const listedIds = await filterListedCollectionIds(collectionHits.map((c) => c.id)).catch(
    () => null
  )
  const listed = listedIds ? new Set(listedIds) : null
  const collections = listed ? collectionHits.filter((c) => listed.has(c.id)) : collectionHits

  return { categories, collections }
}

export interface BlendedSearchResult {
  /** Trgovina products for the requested page, with facets for the sidebar. */
  b2c: SearchResponse
  /** First wholesale-only hits; null when not requested or the B2B search failed. */
  b2b: SearchResponse | null
  categories: SearchCategoryHit[]
  collections: SearchCollectionHit[]
}

/**
 * /search page query: trgovina products (filters apply here), plus wholesale-only
 * products and matching categories/collections when `includeExtras` is set.
 * Only a trgovina failure throws; the extra blocks come back empty instead.
 */
export const searchBlended = async (
  params: SearchParams,
  { includeExtras, b2bLimit }: { includeExtras: boolean; b2bLimit: number }
): Promise<BlendedSearchResult> => {
  const q = params.q ?? ""

  const [b2c, b2b, taxonomy] = await Promise.allSettled([
    searchProductsB2C(params),
    includeExtras
      ? searchB2BOnlyProducts({ q, limit: b2bLimit, offset: 0, sort: params.sort })
      : Promise.resolve(null),
    includeExtras ? searchTaxonomy(q) : Promise.resolve(null),
  ])

  if (b2c.status === "rejected") {
    throw b2c.reason
  }

  const taxonomyResult = taxonomy.status === "fulfilled" ? taxonomy.value : null

  return {
    b2c: b2c.value,
    b2b: b2b.status === "fulfilled" ? b2b.value : null,
    categories: taxonomyResult?.categories ?? [],
    collections: taxonomyResult?.collections ?? [],
  }
}

/**
 * Get search suggestions
 */
export interface SearchSuggestion {
  title: string
  handle: string
  thumbnail?: string
}

export const getSearchSuggestions = async (query: string, limit = 10): Promise<SearchSuggestion[]> => {
  if (!query || query.length < 2) return []
  
  try {
    const response = await sdk.store.product.list({
      q: query,
      limit: Math.min(limit, 20),
      fields: "title,handle,thumbnail"
    })

    const suggestions = response.products?.map((product: any) => ({
      title: product.title,
      handle: product.handle,
      thumbnail: product.thumbnail
    })).filter(Boolean) || []
    
    return suggestions.slice(0, limit)
  } catch (error) {
    return []
  }
}

/**
 * Get bestseller product IDs from MeiliSearch.
 * Accepts both metadata.bestseller and metadata.bestsellerStringValue flags.
 */
export const getBestsellerProductIds = async (limit = 8): Promise<string[]> => {
  try {
    const response = await searchProducts({
      q: "",
      limit,
      offset: 0,
      filter: [
        '(metadata.bestseller IN ["yes","true","1"] OR metadata.bestsellerStringValue = 1 OR metadata.bestsellerStringValue = "1" OR metadata.bestsellerStringValue IN ["yes","true"])',
      ],
      sort: ["created_at:desc"],
    })

    const hits = response?.hits ?? []
    return hits.map((hit: { id?: string }) => hit.id).filter(Boolean) as string[]
  } catch {
    return []
  }
}

/**
 * Get products by category with search capabilities
 */
export const searchProductsByCategory = async (
  categoryHandle: string,
  searchQuery?: string,
  options: Omit<SearchParams, 'categories' | 'q'> = {}
) => {
  return searchProductsB2C({
    ...options,
    q: searchQuery,
    categories: [categoryHandle]
  })
}

/**
 * Enhanced category search that includes subcategories
 */
export const searchProductsByCategoryWithSubcategories = async (
  categoryId: string,
  subcategoryIds: string[],
  searchQuery?: string,
  options: Omit<SearchParams, 'categories' | 'q'> = {}
): Promise<SearchResponse> => {
  const allCategoryIds = [categoryId, ...subcategoryIds]

  try {
    const searchParams: SearchParams = {
      ...options,
      q: searchQuery || "",
      categories: allCategoryIds
    }

    const result = await searchProductsB2C(searchParams)
    
    return result
  } catch (error) {
    throw error
  }
}

/**
 * Get category-specific facets (only facets relevant to products in this category)
 */
export const getCategorySpecificFacets = async (
  categoryId: string,
  subcategoryIds: string[] = [],
  searchParams: SearchParams = {}
): Promise<Record<string, Record<string, number>>> => {
  const allCategoryIds = [categoryId, ...subcategoryIds]
  
  try {
    // Create search params for category context, merging with existing params
    const finalSearchParams: SearchParams = {
      ...searchParams,
      categories: allCategoryIds,
      limit: 0, // We don't need products, just facets
    }
    
    // Get contextual facets for this category, considering existing filters
    const contextualFacets = getContextualFacets(finalSearchParams, true, categoryId)
    finalSearchParams.facets = contextualFacets
    
    const response = await searchProductsB2C(finalSearchParams)

    return response.facetDistribution || {}
  } catch (error) {
    return {}
  }
}

/**
 * Get comprehensive facets for dynamic filters
 */
export const getSearchFacets = async (query?: string, existingFilters?: SearchParams) => {
  try {
    const response = await searchProducts({
      ...existingFilters,
      q: query || "",
      limit: 0, // We don't need products, just facets
      facets: AVAILABLE_FACETS
    })

    return response.facetDistribution || {}
  } catch (error) {
    return {}
  }
}

/**
 * Get all available filter options from the facet distribution
 *
 * @param facetDistribution Meilisearch facet distribution
 * @returns Filter options for the UI
 */
export const getFilterOptions = (facetDistribution: Record<string, Record<string, number>>) => {
  const options: Record<string, { value: string; count: number }[]> = {}
  const fdKeys = Object.keys(facetDistribution)

  for (const [key, attribute] of Object.entries(FILTER_ATTRIBUTE_MAP)) {
    // Find the actual facet key from the distribution, which might be more specific (e.g., "categories.name")
    const facetKey = fdKeys.find(
      (k) => k === attribute || k.startsWith(attribute)
    )

    if (facetKey && facetDistribution[facetKey]) {
      const attributeData = facetDistribution[facetKey]
      
      options[key] = Object.entries(attributeData).map(([value, count]) => ({
        value,
        count,
      }))
    }
  }
  
  return options
}

/**
 * Get contextually relevant facets based on current search context
 * This is the key to smart filtering - only request facets that make sense for the current context
 */
/**
 * Get truly dynamic facets based on progressive filtering
 * This function adapts the requested facets based on current search state and results
 */
export const getDynamicFacets = async (
  searchParams: SearchParams,
  isCategory: boolean = false,
  categoryId?: string
): Promise<string[]> => {
  // Base facets that are always useful for navigation
  const baseFacets = [
    "categories.id",
    "categories.name", 
    "metadata.Blagovna znamka",
    "tags.value"
  ]
  
  // All possible product facets (we'll discover which are relevant)
  const allPossibleFacets = [
    ...baseFacets,
    "metadata.Material",
    "metadata.Glavni material", 
    "metadata.Barva",
    "metadata.Država porekla",
    "metadata.IP zaščita",
    "metadata.Zmogljivost",
    "metadata.Prostornina (l)",
    "metadata.GN velikost",
    "metadata.Napetost",
    "metadata.Povezljivost",
    "metadata.Energiski razred",
    "metadata.Klimatski razdred",
    "metadata.Hladilno sredstvo",
    "metadata.Dolžina kabla",
    "metadata.Vrtljajev na minuto",
    "metadata.Vhodna moč (watt)",
    "metadata.Izhodna moč (watt)",
    "metadata.Primeran za uporabo",
    "metadata.Odtočna pipa"
  ]

  // Count active filters to determine filtering aggressiveness
  const activeFilterCount = Object.entries(searchParams).filter(([key, value]) => {
    if (key === 'q' || key === 'limit' || key === 'offset') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    return value !== undefined && value !== null && value !== ''
  }).length
  
  // Progressive facet reduction based on filtering intensity
  let selectedFacets: string[]
  
  if (activeFilterCount === 0) {
    // No filters applied - be generous with facets to help discovery
    selectedFacets = allPossibleFacets.slice(0, 20)
  } else if (activeFilterCount <= 2) {
    // Light filtering - still show most options
    selectedFacets = allPossibleFacets.slice(0, 15)
  } else if (activeFilterCount <= 4) {
    // Moderate filtering - focus on essential facets
    selectedFacets = [...baseFacets, ...allPossibleFacets.slice(4, 12)]
  } else {
    // Heavy filtering - minimal facets, focus on refinement
    selectedFacets = [...baseFacets, ...allPossibleFacets.slice(4, 8)]
  }
  
  // Always include facets for currently active filter types
  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === 'q' || key === 'limit' || key === 'offset') return
    if ((Array.isArray(value) && value.length > 0) || 
        (typeof value === 'boolean' && value) ||
        (value !== undefined && value !== null && value !== '')) {
      
      const attributeName = FILTER_ATTRIBUTE_MAP[key as keyof typeof FILTER_ATTRIBUTE_MAP]
      if (attributeName && !selectedFacets.includes(attributeName)) {
        selectedFacets.push(attributeName)
      }
    }
  })

  // For categories, add a few more category-specific facets
  if (isCategory) {
    const categoryFacets = ["metadata.Material", "metadata.Barva", "metadata.Zmogljivost"]
    categoryFacets.forEach(facet => {
      if (!selectedFacets.includes(facet)) {
        selectedFacets.push(facet)
      }
    })
  }
  
  // Remove duplicates and apply final limit
  const uniqueFacets = [...new Set(selectedFacets)]
  
  return uniqueFacets
}

/**
 * Legacy function for backward compatibility - now uses dynamic facets
 */
export const getContextualFacets = (
  searchParams: SearchParams,
  isCategory: boolean = false,
  categoryId?: string
): string[] => {
  const activeFilterCount = Object.entries(searchParams).filter(([key, value]) => {
    if (key === 'q' || key === 'limit' || key === 'offset') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    return value !== undefined && value !== null && value !== ''
  }).length
  
  const baseFacets = [
    "categories.id",
    "categories.name", 
    "metadata.Blagovna znamka",
    "tags.value"
  ]
  
  const coreFacets = [
    "metadata.Material",
    "metadata.Glavni material",
    "metadata.Barva", 
    "metadata.Država porekla",
    "metadata.IP zaščita",
    "metadata.Zmogljivost"
  ]
  
  // Always include facets for currently active filter types
  const activeFacets: string[] = []
  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === 'q' || key === 'limit' || key === 'offset') return
    if ((Array.isArray(value) && value.length > 0) || 
        (typeof value === 'boolean' && value) ||
        (value !== undefined && value !== null && value !== '')) {
      
      const attributeName = FILTER_ATTRIBUTE_MAP[key as keyof typeof FILTER_ATTRIBUTE_MAP]
      if (attributeName && !activeFacets.includes(attributeName)) {
        activeFacets.push(attributeName)
      }
    }
  })
  
  // Progressive reduction based on active filters + always include active filter facets
  let selectedFacets: string[]
  
  if (activeFilterCount >= 3) {
    // Heavy filtering - minimal facets + active ones
    selectedFacets = [...baseFacets, ...coreFacets.slice(0, 2), ...activeFacets]
  } else if (activeFilterCount >= 1) {
    // Light filtering - moderate facets + active ones  
    selectedFacets = [...baseFacets, ...coreFacets.slice(0, 4), ...activeFacets]
  } else {
    // No filtering - discovery mode with full facets (include key metadata for dynamic filters)
    selectedFacets = [
      ...baseFacets,
      ...coreFacets,
      "metadata.Prostornina (l)",
      "metadata.GN velikost",
      "metadata.Napetost",
      "metadata.Povezljivost",
      "metadata.Pakiranje",
      "metadata.Enota naročila",
      "metadata.Območje",
    ]
  }
  
  // Remove duplicates
  return [...new Set(selectedFacets)]
}

/**
 * Enhanced search function that includes smart filter analysis
 */
export const searchProductsWithSmartFilters = async (params: SearchParams): Promise<{
  searchResponse: SearchResponse
  smartFilterOptions: Record<string, { value: string; count: number; hidden?: boolean }[]>
}> => {
  // Perform the regular search
  const searchResponse = await searchProducts(params)
  
  // Generate filter options based on the results
  const smartFilterOptions = getFilterOptions(searchResponse.facetDistribution || {})
  
  return {
    searchResponse,
    smartFilterOptions
  }
}

/**
 * Get available facets from MeiliSearch index settings
 */
export const getAvailableFacets = async (): Promise<string[]> => {
  return CLIENT_SAFE_FILTERABLE_FACETS
} 

/**
 * Get available price range from all products (without price filters)
 */
export const getAvailablePriceRange = async (params: Omit<SearchParams, 'minPrice' | 'maxPrice'>): Promise<{ min: number; max: number }> => {
  const index = client.index(B2C_PRODUCTS_INDEX)
  
  // Build filters excluding price range
  const filters = buildMeilisearchFilters({
    ...params,
    minPrice: undefined,
    maxPrice: undefined
  })
  const filterString = filters.length > 0 ? filters.join(" AND ") : undefined

  try {

    
    // Get minimum price by sorting ascending
    const minResponse = await index.search(params.q || "", {
      limit: 1,
      filter: filterString,
      sort: ["prices:asc"],
    })
    
    // Get maximum price by sorting descending
    const maxResponse = await index.search(params.q || "", {
      limit: 1,
      filter: filterString,
      sort: ["prices:desc"],
    })
    

    
    let minPrice = 0
    let maxPrice = 10000
    
    // Extract min price
    if (minResponse.hits.length > 0) {
      const minProduct = minResponse.hits[0]
      if (minProduct.prices && Array.isArray(minProduct.prices) && minProduct.prices.length > 0) {
        minPrice = Math.min(...minProduct.prices.filter((p: number) => p > 0))
      }
    }
    
    // Extract max price
    if (maxResponse.hits.length > 0) {
      const maxProduct = maxResponse.hits[0]
      if (maxProduct.prices && Array.isArray(maxProduct.prices) && maxProduct.prices.length > 0) {
        maxPrice = Math.max(...maxProduct.prices.filter((p: number) => p > 0))
      }
    }
    

    
    return { min: minPrice, max: maxPrice }
  } catch (error) {

    return { min: 0, max: 10000 } // Default fallback
  }
} 